import webApiClient from "./client";

/**
 * Phase 3 Availability API Client
 *
 * Provides clean API methods for instructor availability management with:
 * - Explicit semantic field access (no defensive fallbacks)
 * - V2 API response format compliance
 * - Proper error handling with V2 error codes
 * - TypeScript-like JSDoc documentation
 */

export const availabilityApi = {
    // ====================
    // Recurring Schedules
    // ====================

    /**
     * Create a recurring schedule period (one doc per period with windows[])
     * @param {object} scheduleData - Schedule configuration
     * @param {string} scheduleData.scheduleName - Schedule name (e.g., "Term 1 Spring 2026")
     * @param {string} scheduleData.timezone - Timezone (e.g., "Australia/Brisbane")
     * @param {string} scheduleData.validFrom - Start date (YYYY-MM-DD)
     * @param {string} scheduleData.validUntil - End date (YYYY-MM-DD)
     * @param {string} [scheduleData.defaultLocationId] - Default location ID
     * @param {number} scheduleData.defaultSlotDurationMins - Default duration in minutes (30, 60, 90, 120)
     * @param {Array} scheduleData.windows - Array of {dayOfWeek, startTime, endTime, locationId?, slotDurationMins?}
     * @param {object} [scheduleData.lunchBreak] - {enabled, startTime, endTime}
     * @returns {Promise<object>} Created schedule document
     * @throws {Error} If creation fails or response is invalid
     */
    createRecurringSchedule: async scheduleData => {
        const response = await webApiClient.post("/api/v2/availability/recurring", scheduleData);

        if (!response.ok || !response.data || !response.data.schedule) {
            throw new Error(response.data?.message || "Failed to create schedule");
        }

        return response.data.schedule; // ✅ Explicit semantic field access
    },

    /**
     * Get all recurring schedules for an instructor
     * @param {string} teacherId - Instructor's organization ID
     * @returns {Promise<Array>} Array of recurring schedules
     * @throws {Error} If fetch fails or response is invalid
     */
    getRecurringSchedules: async teacherId => {
        const response = await webApiClient.get(`/api/v2/availability/recurring/${teacherId}`);

        if (!response.ok || !response.data || !response.data.schedules) {
            throw new Error(response.data?.message || "Failed to fetch schedules");
        }

        return response.data.schedules; // ✅ Plural semantic field
    },

    /**
     * Publish standard work week as recurring schedules
     * @param {object} publishData - Publishing configuration (teacherId from JWT, not body)
     * @param {object} publishData.standardSchedule - Standard work week template
     * @param {Array<string>} publishData.standardSchedule.workDays - Work days (abbreviated: "Sun", "Mon", etc.)
     * @param {string} publishData.standardSchedule.startTime - Work start time (HH:MM)
     * @param {string} publishData.standardSchedule.endTime - Work end time (HH:MM)
     * @param {number} publishData.standardSchedule.lessonDuration - Lesson duration in minutes
     * @param {object} [publishData.standardSchedule.lunchBreak] - Lunch break configuration
     * @param {string} [publishData.primaryLocationId] - Primary location ID
     * @param {string} [publishData.timezone] - Timezone (default "Australia/Sydney")
     * @param {string} publishData.validFrom - Start date (ISO 8601)
     * @param {string} publishData.validUntil - End date (ISO 8601)
     * @param {boolean} [publishData.clearExisting] - Clear existing schedules first
     * @param {string} [publishData.scheduleName] - Optional schedule name (defaults to auto-generated: "Schedule - [date]")
     * @returns {Promise<object>} Publish result with created schedules count
     * @throws {Error} If publishing fails or response is invalid
     */
    publishSchedule: async publishData => {
        const response = await webApiClient.post("/api/v2/availability/recurring/publish-schedule", publishData);

        if (!response.ok || !response.data) {
            throw new Error(response.data?.message || "Failed to publish schedule");
        }

        return response.data;
    },

    /**
     * Update a recurring schedule period by ID (partial update)
     * @param {string} scheduleId - Schedule document ID
     * @param {object} updates - Fields to update (at least one required)
     * @param {string} [updates.scheduleName] - New schedule name
     * @param {string} [updates.validFrom] - New start date (YYYY-MM-DD)
     * @param {string} [updates.validUntil] - New end date (YYYY-MM-DD)
     * @param {Array} [updates.windows] - Full replacement windows[] array
     * @param {object} [updates.lunchBreak] - New lunch break settings
     * @param {number} [updates.defaultSlotDurationMins] - New default duration
     * @param {string} [updates.defaultLocationId] - New default location ID
     * @returns {Promise<object>} Updated schedule document
     * @throws {Error} If update fails or response is invalid
     */
    updateRecurringSchedule: async (scheduleId, updates) => {
        const response = await webApiClient.put(`/api/v2/availability/recurring/${scheduleId}`, updates);

        if (!response.ok || !response.data || !response.data.schedule) {
            throw new Error(response.data?.message || "Failed to update schedule");
        }

        return response.data.schedule;
    },

    /**
     * Delete a recurring schedule by ID
     * @param {string} scheduleId - Schedule document ID
     * @returns {Promise<void>}
     * @throws {Error} If deletion fails
     */
    deleteRecurringSchedule: async scheduleId => {
        const response = await webApiClient.delete(`/api/v2/availability/recurring/${scheduleId}`);

        if (!response.ok) {
            throw new Error(response.data?.message || "Failed to delete schedule");
        }
    },

    // ====================
    // Availability Overrides
    // ====================

    /**
     * Create an availability override for a specific date.
     * Overrides are single-day, authoritative exceptions that fully
     * replace normal availability resolution for that date.
     *
     * Typical uses:
     * - Sick days
     * - Emergency closures
     * - One-off custom availability slots
     *
     * @param {object} overrideData - Override configuration
     * @param {string} overrideData.date - ISO 8601 date string (single day only)
     * @param {Array} [overrideData.slots] - Optional array of custom time slots
     * @param {boolean} [overrideData.isClosed] - Mark entire day as closed
     * @param {string} [overrideData.reason] - Optional reason for the override
     * @returns {Promise<object>} Created override document
     * @throws {Error} If creation fails or response is invalid
     */
    createOverride: async overrideData => {
        const response = await webApiClient.post("/api/v2/availability/overrides", overrideData);

        if (!response.ok || !response.data || !response.data.override) {
            throw new Error(response.data?.message || "Failed to create override");
        }

        return response.data.override;
    },

    /**
     * Get availability overrides for an instructor
     * @param {string} teacherId - Instructor's organization ID
     * @param {object} [options] - Query options
     * @param {string} [options.startDate] - Filter start date (ISO 8601)
     * @param {string} [options.endDate] - Filter end date (ISO 8601)
     * @returns {Promise<Array>} Array of override documents
     * @throws {Error} If fetch fails or response is invalid
     */
    getOverrides: async (teacherId, options = {}) => {
        const params = new URLSearchParams();
        if (options.startDate) params.append("startDate", options.startDate);
        if (options.endDate) params.append("endDate", options.endDate);

        const queryString = params.toString();
        const url = `/api/v2/availability/overrides/${teacherId}${queryString ? "?" + queryString : ""}`;

        const response = await webApiClient.get(url);

        if (!response.ok || !response.data || !response.data.overrides) {
            throw new Error(response.data?.message || "Failed to fetch overrides");
        }

        return response.data.overrides;
    },

    /**
     * Update an availability override by ID
     * @param {string} overrideId - Override document ID
     * @param {object} updates - Fields to update
     * @param {Array} [updates.slots] - New time slots
     * @param {boolean} [updates.isClosed] - New closed status
     * @param {string} [updates.travelPeriodId] - New travel period link
     * @returns {Promise<object>} Updated override document
     * @throws {Error} If update fails or response is invalid
     */
    updateOverride: async (overrideId, updates) => {
        const response = await webApiClient.put(`/api/v2/availability/overrides/${overrideId}`, updates);

        if (!response.ok || !response.data || !response.data.override) {
            throw new Error(response.data?.message || "Failed to update override");
        }

        return response.data.override;
    },

    /**
     * Nudge a day's slots: shift colliding slots forward and add new ones atomically.
     * @param {object} payload
     * @param {string} payload.date - ISO date (YYYY-MM-DD)
     * @param {Array} [payload.addSlots] - New slots to add ({startTime,endTime,slotDuration})
     * @param {Array} [payload.shifts] - Slots to move ({oldStartTime,newStartTime,newEndTime,slotDuration})
     * @returns {Promise<object>} Updated override document
     * @throws {Error} If the nudge fails (e.g. a moved slot has a booking)
     */
    nudgeSlots: async payload => {
        const response = await webApiClient.patch("/api/v2/availability/overrides/nudge", payload);

        if (!response.ok || !response.data || !response.data.override) {
            throw new Error(response.data?.message || "Failed to make room");
        }

        return response.data.override;
    },

    /**
     * Delete an availability override by ID
     * @param {string} overrideId - Override document ID
     * @returns {Promise<void>}
     * @throws {Error} If deletion fails
     */
    deleteOverride: async overrideId => {
        const response = await webApiClient.delete(`/api/v2/availability/overrides/${overrideId}`);

        if (!response.ok) {
            throw new Error(response.data?.message || "Failed to delete override");
        }
    },

    /**
     * Get travel periods for an instructor using availability contexts
     * @param {string} teacherId - Instructor's organization ID
     * @param {object} [options] - Query options
     * @param {string} [options.startDate] - Filter start date (ISO 8601)
     * @param {string} [options.endDate] - Filter end date (ISO 8601)
     * @returns {Promise<Array>} Array of travel context documents
     * @throws {Error} If fetch fails or response is invalid
     */
    getTravelPeriods: async (teacherId, options = {}) => {
        const params = new URLSearchParams();
        params.append("context", "travel"); // Filter for travel contexts only
        if (options.startDate) params.append("startDate", options.startDate);
        if (options.endDate) params.append("endDate", options.endDate);

        const queryString = params.toString();
        const url = `/api/v2/availability/contexts/${teacherId}${queryString ? "?" + queryString : ""}`;

        const response = await webApiClient.get(url);

        if (!response.ok || !response.data || !response.data.contexts) {
            throw new Error(response.data?.message || "Failed to fetch travel periods");
        }

        return response.data.contexts;
    },


    // ====================
    // Availability Contexts (NEW CANONICAL - replaces blockDates + travel concept)
    // ====================

    /**
     * Create availability context (holidays, leave, travel, personal time)
     * Range-based context with fully editable dates and reason
     * @param {object} contextData - Context configuration
     * @param {string} contextData.startDate - Start date (YYYY-MM-DD)
     * @param {string} contextData.endDate - End date (YYYY-MM-DD)
     * @param {string} contextData.context - Context type: "holiday" | "leave" | "travel" | "personal"
     * @param {string} contextData.mode - Availability mode: "open" | "closed" | "restricted"
     * @param {string} contextData.reason - Human-readable reason (e.g., "Christmas Holiday")
     * @param {string} [contextData.destination] - Destination (for travel contexts)
     * @param {object} [contextData.location] - Location data (for travel contexts)
     * @returns {Promise<object>} Created context document
     * @throws {Error} If creation fails or response is invalid
     */
    createContext: async contextData => {
        const response = await webApiClient.post("/api/v2/availability/contexts", contextData);

        if (!response.ok || !response.data || !response.data.context) {
            throw new Error(response.data?.message || "Failed to create context");
        }

        return response.data.context;
    },

    /**
     * Check for existing home bookings in a date range (pre-travel-period conflict check)
     * @param {string} startDate - ISO date string
     * @param {string} endDate - ISO date string
     * @returns {Promise<Array>} Array of conflicting bookings
     */
    // Every active booking in the range, whatever it's tagged with. When editing a period,
    // pass its id so its own bookings aren't reported as clashes.
    getHomeBookingsInDateRange: async (startDate, endDate, excludeContextId = null) => {
        const exclude = excludeContextId ? `&excludeContextId=${excludeContextId}` : '';
        const response = await webApiClient.get(
            `/api/v2/availability/contexts/home-bookings-check?startDate=${startDate}&endDate=${endDate}${exclude}`
        );
        if (!response.ok) {
            throw new Error(response.data?.message || 'Failed to check home bookings');
        }
        return response.data.bookings || [];
    },

    /**
     * Get availability contexts for the authenticated instructor.
     * Identity is extracted from the JWT — no ID parameter needed.
     * @param {object} [options] - Query options
     * @param {string} [options.context] - Filter by context type
     * @param {string} [options.startDate] - Filter start date (ISO 8601)
     * @param {string} [options.endDate] - Filter end date (ISO 8601)
     * @returns {Promise<Array>} Array of context documents
     * @throws {Error} If fetch fails or response is invalid
     */
    getContexts: async (options = {}) => {
        const params = new URLSearchParams();
        if (options.context) params.append("context", options.context);
        if (options.startDate) params.append("startDate", options.startDate);
        if (options.endDate) params.append("endDate", options.endDate);

        const queryString = params.toString();
        const url = `/api/v2/availability/contexts${queryString ? "?" + queryString : ""}`;

        const response = await webApiClient.get(url);

        if (!response.ok || !response.data || !response.data.contexts) {
            throw new Error(response.data?.message || "Failed to fetch contexts");
        }

        return response.data.contexts;
    },

    /**
     * Update availability context by ID
     * Fully editable: can change dates, reason, mode, context type
     * @param {string} contextId - Context document ID
     * @param {object} updates - Fields to update
     * @param {string} [updates.startDate] - New start date (YYYY-MM-DD)
     * @param {string} [updates.endDate] - New end date (YYYY-MM-DD)
     * @param {string} [updates.reason] - New reason
     * @param {string} [updates.context] - New context type
     * @param {string} [updates.mode] - New availability mode
     * @returns {Promise<object>} Updated context document
     * @throws {Error} If update fails or response is invalid
     */
    updateContext: async (contextId, updates) => {
        const response = await webApiClient.patch(`/api/v2/availability/contexts/${contextId}`, updates);

        if (!response.ok || !response.data || !response.data.context) {
            // Carry the typed code (TRAVEL_CURRENCY_LOCKED / PAID_BOOKING_REQUIRES_RETURN) so
            // callers branch on it, never on the message text.
            const error = new Error(response.data?.message || "Failed to update context");
            error.status = response.status;
            error.errorCode = response.data?.code || response.data?.errorCode;
            error.details = response.data?.details;
            throw error;
        }

        return response.data.context;
    },

    /**
     * Delete availability context by ID
     * @param {string} contextId - Context document ID
     * @returns {Promise<void>}
     * @throws {Error} If deletion fails
     */
    deleteContext: async contextId => {
        const response = await webApiClient.delete(`/api/v2/availability/contexts/${contextId}`);

        if (!response.ok) {
            throw new Error(response.data?.message || "Failed to delete context");
        }
    },

    /**
     * Get active bookings for a travel period (pre-delete confirmation UI)
     * @param {string} contextId
     * @returns {Promise<Array>} bookings array
     */
    getTravelPeriodBookings: async (contextId) => {
        const response = await webApiClient.get(
            `/api/v2/availability/contexts/${contextId}/bookings`
        );
        if (!response.ok) {
            throw new Error(response.data?.message || 'Failed to fetch travel period bookings');
        }
        return response.data.bookings;
    },

    /**
     * Cancel selected bookings and delete the travel period
     * @param {string} contextId
     * @param {string[]} bookingIds
     * @param {string} message - Cancellation message to send to students
     * @returns {Promise<object>} { success, message, bookingsCancelled, overridesDeleted, contextDeleted }
     */
    cancelAndDeleteTravelPeriod: async (contextId, bookingIds, message) => {
        const response = await webApiClient.post(
            `/api/v2/availability/contexts/${contextId}/cancel-and-delete`,
            { bookingIds, message }
        );
        if (!response.ok) {
            // Carry the typed code (TRAVEL_CURRENCY_LOCKED / PAID_BOOKING_REQUIRES_RETURN) so
            // callers branch on it, never on the message text.
            const error = new Error(response.data?.message || 'Failed to cancel and delete travel period');
            error.status = response.status;
            error.errorCode = response.data?.code || response.data?.errorCode;
            error.details = response.data?.details;
            throw error;
        }
        return response.data;
    },

    /**
     * Cancel home-schedule bookings for a travel period (edit-path conflict resolution).
     * Does NOT delete the travel period.
     * @param {string} contextId
     * @param {string[]} bookingIds
     * @param {string} message
     * @returns {Promise<object>} { bookingsCancelled }
     */
    bulkCancelTravelBookings: async (contextId, bookingIds, message) => {
        const response = await webApiClient.post(
            `/api/v2/availability/contexts/${contextId}/bulk-cancel-bookings`,
            { bookingIds, message }
        );
        if (!response.ok) {
            // Carry the typed code (TRAVEL_CURRENCY_LOCKED / PAID_BOOKING_REQUIRES_RETURN) so
            // callers branch on it, never on the message text.
            const error = new Error(response.data?.message || 'Failed to cancel bookings for travel period');
            error.status = response.status;
            error.errorCode = response.data?.code || response.data?.errorCode;
            error.details = response.data?.details;
            throw error;
        }
        return response.data;
    },

    /**
     * Record sick day - blocks today and cancels any existing bookings
     * Creates isClosed override and notifies affected students
     * @param {object} sickDayData - Sick day configuration (teacherId from JWT, not body)
     * @param {string} [sickDayData.date] - ISO date string for the sick day (defaults to today on backend if omitted)
     * @param {string} [sickDayData.reason] - Optional cancellation reason
     * @param {string[]} [sickDayData.bookingIds] - Optional specific booking IDs to cancel
     * @returns {Promise<object>} Sick day result with cancellation count
     * @throws {Error} If operation fails or response is invalid
     */
    sickDay: async sickDayData => {
        const response = await webApiClient.post("/api/v2/availability/overrides/sick-day", sickDayData);

        if (!response.ok || !response.data) {
            throw new Error(response.data?.message || "Failed to record sick day");
        }

        return response.data; // { success: true, message, sickDay: { dateBlocked, bookingsCancelled, studentsNotified } }
    },

    // ====================
    // Merged Calendar View
    // ====================

    /**
     * Get merged calendar view with all availability sources
     * Combines recurring schedules, overrides, and travel periods
     * @param {string} teacherId - Instructor's organization ID
     * @param {string} startDate - Start date (ISO 8601 YYYY-MM-DD)
     * @param {string} endDate - End date (ISO 8601 YYYY-MM-DD)
     * @returns {Promise<object>} Date-keyed object with slots arrays for each date
     * @throws {Error} If fetch fails or response is invalid
     */
    getMergedCalendar: async (teacherId, startDate, endDate) => {
        const params = new URLSearchParams({ startDate, endDate });
        const response = await webApiClient.get(`/api/v2/availability/${teacherId}/calendar?${params}`);

        if (!response.ok || !response.data || !response.data.calendar) {
            throw new Error(response.data?.message || "Failed to fetch calendar");
        }

        return response.data.calendar; // ✅ Semantic field - date-keyed object
    },

    /**
     * Get filtered management calendar for instructor UI (AUTHENTICATED)
     * @param {string} startDate - Start date (ISO 8601 YYYY-MM-DD)
     * @param {string} endDate - End date (ISO 8601 YYYY-MM-DD)
     * @param {string} mode - Calendar mode ('home' | 'travel')
     * @param {string} [contextId] - Travel context ID (required if mode='travel')
     * @returns {Promise<object>} Date-keyed object with filtered slots
     * @throws {Error} If fetch fails or response is invalid
     */
    getManagementCalendar: async (startDate, endDate, mode, contextId = null) => {
        const params = new URLSearchParams({ startDate, endDate, mode });
        if (contextId) params.append('contextId', contextId);

        const response = await webApiClient.get(`/api/v2/availability/management-calendar?${params}`);

        if (!response.ok || !response.data || !response.data.calendar) {
            throw new Error(response.data?.message || "Failed to fetch management calendar");
        }

        return response.data.calendar;
    },
};
